import { IconName } from '../../../shared/icons/icon-map';

// Keys must match the folder/file naming under src/assets/animations/,
// e.g. 'water_pump' -> assets/animations/water_pump/ic_animation_water_pump.json
export type ShowcaseUseCase =
  | 'illu_sprinkler'
  | 'illu_rain_collect'
  | 'illu_water_pump'
  | 'illu_water_tree';

interface DemoOptionCopyBase {
  headline: string;
  description: string;
  icon?: IconName;
  useCase?: ShowcaseUseCase;
  success?: string;
  selectable?: boolean;
  nextSuggestion?: {
    targetId: string;
    messageKey: string;
  };
}

export interface DemoLeafOptionCopy extends DemoOptionCopyBase {
  id: string | undefined;
  options?: never;
}

export interface DemoGroupOptionCopy extends DemoOptionCopyBase {
  options: DemoLeafOptionCopy[];
  id?: undefined;
}

export type DemoOptionCopy = DemoLeafOptionCopy | DemoGroupOptionCopy;

export interface DemoLeafOption extends DemoLeafOptionCopy {
  loadingDurationSeconds: number;
}

export interface DemoGroupOption extends DemoGroupOptionCopy {
  options: DemoLeafOption[];
}

export type DemoOption = DemoLeafOption | DemoGroupOption;

export const INITIAL_SCENARIO_ID = '0d906c9a-9c8b-11f1-994c-32c1f8658350';

const ALL_DEMO_OPTIONS: DemoOptionCopy[] = [
  {
    headline: 'demo.dialog.steps.loadingInitial.headline',
    description: 'demo.dialog.steps.loadingInitial.body',
    useCase: 'illu_rain_collect',
    id: INITIAL_SCENARIO_ID,
    selectable: false,
  },
  {
    headline: 'demo.dialog.options.sprinkler.headline',
    description: 'demo.dialog.options.sprinkler.description',
    icon: 'ic_gras',
    useCase: 'illu_sprinkler',
    id: '64edc2ff-4dde-11f1-994c-32c1f8658350',
    success: 'demo.dialog.options.sprinkler.success',
    nextSuggestion: {
      targetId: '64edbfaa-4dde-11f1-994c-32c1f8658350',
      messageKey: 'demo.dialog.steps.nextSuggestion.sprinklerToTree',
    },
  },
  {
    headline: 'demo.dialog.options.treeWatering.headline',
    description: 'demo.dialog.options.treeWatering.description',
    icon: 'ic_tree',
    useCase: 'illu_water_tree',
    success: 'demo.dialog.options.treeWatering.success',
    nextSuggestion: {
      targetId: '64edc2ff-4dde-11f1-994c-32c1f8658350',
      messageKey: 'demo.dialog.steps.nextSuggestion.treeToSprinkler',
    },
    options: [
      {
        headline: 'demo.dialog.options.treeWatering.10.headline',
        description: 'demo.dialog.options.treeWatering.10.description',
        icon: 'ic_tree',
        id: '64edbfaa-4dde-11f1-994c-32c1f8658350',
        success: 'demo.dialog.options.treeWatering.10.success',
      },
      {
        headline: 'demo.dialog.options.treeWatering.20.headline',
        description: 'demo.dialog.options.treeWatering.20.description',
        icon: 'ic_tree',
        id: 'fa57b21b-9bd4-11f1-994c-32c1f8658350',
        success: 'demo.dialog.options.treeWatering.20.success',
      },
    ],
  },
  {
    headline: 'demo.dialog.options.pumping.headline',
    description: 'demo.dialog.options.pumping.description',
    icon: 'ic_pump',
    useCase: 'illu_water_pump',
    id: '6c9cb846-4dde-11f1-994c-32c1f8658350',
    success: 'demo.dialog.options.pumping.success',
  },
];

// User-facing selection list (e.g. DialogDemoComponent.resolveOptions()) -
// entries marked `selectable: false` are kept out of it.
export const DEMO_OPTIONS: DemoOptionCopy[] = ALL_DEMO_OPTIONS.filter(
  (option) => option.selectable !== false,
);

/**
 * Finds the copy entry for a scenario id, checking top-level entries and
 * group sub-options alike (across all entries, including non-selectable
 * ones). A sub-option without its own `useCase` inherits its group's
 * (mirrors DialogDemoComponent.resolveOptions()).
 */
export function findDemoOptionCopyById(
  id: string,
): DemoLeafOptionCopy | undefined {
  for (const option of ALL_DEMO_OPTIONS) {
    if (option.id === id) {
      return option;
    }

    const subOption = option.options?.find((leaf) => leaf.id === id);
    if (subOption) {
      return { ...subOption, useCase: subOption.useCase ?? option.useCase };
    }
  }

  return undefined;
}
